import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, productImageUrl, formatApiError, BACKEND_URL } from "../lib/api";
import {
  Star, ShoppingCart, ShieldCheck, Truck, BadgePercent, Sparkles, Zap,
  ChevronLeft, ChevronRight, ZoomIn, Heart, GitCompare, Award, RefreshCcw,
  Camera, X as XIcon, Minus, Plus, MessageCircle, CreditCard, Package, HeadphonesIcon, Clock,
  Share2, Facebook, Copy, Check,
} from "lucide-react";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { useFavorites, useCompare } from "../context/FavoritesCompareContext";
import { useCurrency } from "../context/CurrencyContext";
import { toast } from "sonner";
import ProductCard from "../components/ProductCard";

const RECENT_KEY = "je_recent_products_v1";

const readRecent = () => {
  try { return JSON.parse(localStorage.getItem(RECENT_KEY) || "[]"); } catch { return []; }
};
const pushRecent = (id) => {
  try {
    const list = readRecent().filter((x) => x !== id);
    list.unshift(id);
    localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, 12)));
  } catch { /* ignore */ }
};

export default function ProductDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { format: formatGdes } = useCurrency();
  const [product, setProduct] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [similar, setSimilar] = useState([]);
  const [recent, setRecent] = useState([]);
  const [activeImg, setActiveImg] = useState(0);
  const [qty, setQty] = useState(1);
  const [colorQty, setColorQty] = useState({}); // for cable: { colorName: qty }
  const [technicianQty, setTechnicianQty] = useState(0);
  const [selectedOptions, setSelectedOptions] = useState({});
  const [tab, setTab] = useState("description");
  const [zoom, setZoom] = useState({ show: false, x: 50, y: 50 });
  const [lightbox, setLightbox] = useState(false);
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewComment, setReviewComment] = useState("");
  const [reviewImages, setReviewImages] = useState([]);
  const [uploadingReview, setUploadingReview] = useState(false);
  const [reviewLightbox, setReviewLightbox] = useState(null);
  const reviewFileRef = useRef(null);
  const { addItem } = useCart();
  const { user } = useAuth();
  const fav = useFavorites();
  const cmp = useCompare();

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/products/${id}`);
        setProduct(data);
        setActiveImg(0);
        pushRecent(id);
        const r = await api.get("/reviews", { params: { product_id: id } });
        setReviews(r.data);
        const sim = await api.get("/products", { params: { category: data.category, limit: 12 } });
        setSimilar((sim.data || []).filter((p) => p.product_id !== id).slice(0, 6));
        // Recently viewed (exclude current)
        const recentIds = readRecent().filter((x) => x !== id).slice(0, 6);
        if (recentIds.length) {
          const all = await api.get("/products", { params: { limit: 200 } });
          const map = new Map((all.data || []).map((p) => [p.product_id, p]));
          setRecent(recentIds.map((rid) => map.get(rid)).filter(Boolean).slice(0, 6));
        } else {
          setRecent([]);
        }
      } catch { /* ignore */ }
    })();
  }, [id]);

  if (!product) return <div className="je-container py-20 text-center text-gray-500">Chargement…</div>;

  const discounted = product.is_promo && product.discount_percent > 0
    ? Math.round(product.price * (100 - product.discount_percent)) / 100
    : null;
  const currentPrice = discounted ?? product.price;
  const oldPrice = product.compare_at_price && product.compare_at_price > product.price
    ? product.compare_at_price
    : (discounted ? product.price : null);
  const hasDiscount = oldPrice != null;
  const discountPct = hasDiscount
    ? (product.discount_percent > 0
        ? product.discount_percent
        : Math.round(((oldPrice - currentPrice) / oldPrice) * 100))
    : 0;
  const isCable = product.product_type === "cable";
  const cableColors = Array.isArray(product.available_colors) ? product.available_colors : [];
  const images = product.images?.length ? product.images : [null];

  const goPrev = () => setActiveImg((i) => (i - 1 + images.length) % images.length);
  const goNext = () => setActiveImg((i) => (i + 1) % images.length);

  const submitReview = async (e) => {
    e.preventDefault();
    if (!user) { toast.error("Connectez-vous pour laisser un avis"); return; }
    try {
      await api.post("/reviews", { product_id: id, rating: reviewRating, comment: reviewComment, name: user?.name, images: reviewImages });
      toast.success("Avis publié, merci !");
      setReviewComment("");
      setReviewImages([]);
      const r = await api.get("/reviews", { params: { product_id: id } });
      setReviews(r.data);
      const p = await api.get(`/products/${id}`);
      setProduct(p.data);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const onPickReviewPhotos = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    if (reviewImages.length + files.length > 4) { toast.error("Maximum 4 photos par avis"); return; }
    setUploadingReview(true);
    try {
      const uploads = await Promise.all(files.map(async (f) => {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/upload/review-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
        return data.url;
      }));
      setReviewImages([...reviewImages, ...uploads]);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploadingReview(false);
      if (reviewFileRef.current) reviewFileRef.current.value = "";
    }
  };

  const reviewImgUrl = (u) => u.startsWith("http") ? u : `${BACKEND_URL}${u}`;

  const handleAddToCart = () => {
    const optionsList = product.options || [];
    if (optionsList.length > 0) {
      const missing = optionsList.find((o) => (o.values || []).length > 0 && !selectedOptions[o.name]);
      if (missing) { toast.error(`Veuillez choisir : ${missing.name}`); return; }
    }
    const selOpts = optionsList.length > 0 ? { ...selectedOptions } : null;
    if (isCable && cableColors.length > 0) {
      const breakdown = Object.fromEntries(
        Object.entries(colorQty).filter(([, v]) => Number(v) > 0).map(([k, v]) => [k, Number(v)])
      );
      const total = Object.values(breakdown).reduce((s, n) => s + n, 0);
      if (total <= 0) { toast.error("Choisissez au moins 1 pied pour une couleur"); return; }
      addItem({ ...product, color_breakdown: breakdown, technician_qty: technicianQty, selected_options: selOpts }, total);
      toast.success(`${total} pied(s) ajouté(s) au panier`);
      return;
    }
    addItem({ ...product, technician_qty: technicianQty, selected_options: selOpts }, qty);
    toast.success(`${qty} × ${product.name} ajouté au panier`);
  };
  const handleBuyNow = () => {
    const optionsList = product.options || [];
    if (optionsList.length > 0) {
      const missing = optionsList.find((o) => (o.values || []).length > 0 && !selectedOptions[o.name]);
      if (missing) { toast.error(`Veuillez choisir : ${missing.name}`); return; }
    }
    const selOpts = optionsList.length > 0 ? { ...selectedOptions } : null;
    if (isCable && cableColors.length > 0) {
      const breakdown = Object.fromEntries(
        Object.entries(colorQty).filter(([, v]) => Number(v) > 0).map(([k, v]) => [k, Number(v)])
      );
      const total = Object.values(breakdown).reduce((s, n) => s + n, 0);
      if (total <= 0) { toast.error("Choisissez au moins 1 pied pour une couleur"); return; }
      addItem({ ...product, color_breakdown: breakdown, technician_qty: technicianQty, selected_options: selOpts }, total);
      navigate("/cart");
      return;
    }
    addItem({ ...product, technician_qty: technicianQty, selected_options: selOpts }, qty);
    navigate("/cart");
  };

  const CATEGORY_LABELS = {
    telephones: "Téléphones",
    audio: "Audio & Casques",
    electromenager: "Électroménager",
    "energie-solaire": "Énergie solaire",
    batteries: "Batteries",
  };
  const catLabel = CATEGORY_LABELS[product.category] || product.category;

  const soldCount = product.reviews_count > 0 ? product.reviews_count * 4 + 12 : 0;

  return (
    <div className="bg-[#F8FAFC] pb-16" data-testid="product-detail-page">
      <div className="je-container py-6">
        {/* Breadcrumb */}
        <nav className="text-xs text-gray-500 mb-5 flex items-center gap-1.5 flex-wrap" aria-label="breadcrumb">
          <Link to="/" className="hover:text-[#0052CC]">Accueil</Link>
          <ChevronRight className="w-3 h-3" />
          <Link to={`/products?category=${product.category}`} className="hover:text-[#0052CC]">{catLabel}</Link>
          <ChevronRight className="w-3 h-3" />
          <span className="text-gray-700 font-semibold line-clamp-1">{product.name}</span>
        </nav>

        {/* Main product section */}
        <div className="grid lg:grid-cols-[minmax(0,1fr)_minmax(0,420px)] xl:grid-cols-[minmax(0,1fr)_minmax(0,460px)] gap-6 lg:gap-10">
          {/* LEFT — Gallery */}
          <div className="flex gap-3 md:gap-4">
            {/* Thumbnails vertical (desktop) / hidden mobile */}
            <div className="hidden md:flex flex-col gap-2 w-16 lg:w-20 shrink-0" data-testid="gallery-thumbs">
              {images.slice(0, 5).map((img, i) => (
                <button
                  key={i}
                  onClick={() => setActiveImg(i)}
                  className={`relative w-full aspect-square rounded-lg overflow-hidden border-2 transition ${i === activeImg ? "border-[#E3000F] shadow-md" : "border-gray-200 hover:border-[#0052CC]"}`}
                  data-testid={`thumb-${i}`}
                >
                  <img src={productImageUrl(img)} alt="" className="absolute inset-0 w-full h-full object-cover" />
                </button>
              ))}
              {images.length > 5 && (
                <button
                  onClick={() => setActiveImg(5)}
                  className={`w-full aspect-square rounded-lg overflow-hidden border-2 border-gray-200 hover:border-[#0052CC] bg-black/70 text-white flex items-center justify-center font-bold text-sm`}
                >
                  +{images.length - 5}
                </button>
              )}
            </div>

            {/* Main image */}
            <div className="flex-1 min-w-0 relative">
              {/* Discount badge */}
              {hasDiscount && (
                <span className="absolute top-3 left-3 z-10 bg-[#E3000F] text-white text-xs font-bold px-2.5 py-1 rounded-md shadow" data-testid="hero-discount-badge">
                  -{discountPct}%
                </span>
              )}
              {/* Zoom button */}
              <button
                onClick={() => setLightbox(true)}
                className="absolute top-3 right-3 z-10 bg-white/90 hover:bg-white text-gray-700 rounded-full p-2 shadow-md transition"
                aria-label="Zoom"
                data-testid="zoom-btn"
              >
                <ZoomIn className="w-4 h-4" />
              </button>
              <div
                className="relative w-full aspect-square rounded-2xl overflow-hidden bg-white border border-gray-200 group cursor-zoom-in"
                onMouseEnter={() => setZoom((z) => ({ ...z, show: true }))}
                onMouseLeave={() => setZoom({ show: false, x: 50, y: 50 })}
                onMouseMove={(e) => {
                  const rect = e.currentTarget.getBoundingClientRect();
                  const x = ((e.clientX - rect.left) / rect.width) * 100;
                  const y = ((e.clientY - rect.top) / rect.height) * 100;
                  setZoom({ show: true, x, y });
                }}
                data-testid="main-image-container"
              >
                <img
                  src={productImageUrl(images[activeImg])}
                  alt={product.name}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-300"
                  style={zoom.show ? {
                    transform: `scale(2)`,
                    transformOrigin: `${zoom.x}% ${zoom.y}%`,
                  } : undefined}
                />
              </div>

              {/* Prev/next arrows */}
              {images.length > 1 && (
                <>
                  <button
                    onClick={goPrev}
                    className="absolute left-2 top-1/2 -translate-y-1/2 z-10 bg-white/90 hover:bg-white rounded-full p-2 shadow-md transition"
                    aria-label="Précédent"
                    data-testid="gallery-prev"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <button
                    onClick={goNext}
                    className="absolute right-2 top-1/2 -translate-y-1/2 z-10 bg-white/90 hover:bg-white rounded-full p-2 shadow-md transition"
                    aria-label="Suivant"
                    data-testid="gallery-next"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </>
              )}

              {/* Mobile thumbnails (horizontal) */}
              <div className="md:hidden flex gap-2 mt-3 overflow-x-auto">
                {images.map((img, i) => (
                  <button
                    key={i}
                    onClick={() => setActiveImg(i)}
                    className={`shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 ${i === activeImg ? "border-[#E3000F]" : "border-gray-200"}`}
                  >
                    <img src={productImageUrl(img)} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* RIGHT — Info */}
          <div className="min-w-0" data-testid="product-info">
            {/* Badges */}
            <div className="flex flex-wrap gap-2 mb-3">
              {product.is_new && (
                <span className="inline-flex items-center gap-1 bg-blue-50 text-[#0052CC] text-xs font-bold uppercase tracking-widest px-3 py-1 rounded-full">
                  <Sparkles className="w-3 h-3" /> Nouveau
                </span>
              )}
              {product.is_bestseller && (
                <span className="inline-flex items-center gap-1 bg-amber-50 text-amber-700 text-xs font-bold uppercase tracking-widest px-3 py-1 rounded-full">
                  ★ Best-seller
                </span>
              )}
            </div>

            <h1 className="text-2xl md:text-3xl lg:text-[28px] font-extrabold leading-tight text-gray-900" style={{ fontFamily: "Outfit" }}>
              {product.name}
            </h1>

            <div className="flex flex-wrap items-center gap-2 mt-2 text-sm text-gray-500">
              {product.brand && (
                <>
                  <span>Marque : <span className="font-semibold text-gray-900">{product.brand}</span></span>
                  <span className="text-gray-300">|</span>
                </>
              )}
              <span>Catégorie : <Link to={`/products?category=${product.category}`} className="font-semibold text-[#0052CC] hover:underline">{catLabel}</Link></span>
              <span className="text-gray-300">|</span>
              <span>SKU : <span className="font-mono text-gray-700">{product.product_id.slice(-10).toUpperCase()}</span></span>
            </div>

            <div className="flex flex-wrap items-center gap-3 mt-3" data-testid="ratings-row">
              <div className="flex items-center gap-1.5 bg-amber-50 px-2.5 py-1 rounded-md">
                <div className="flex">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Star key={i} className={`w-3.5 h-3.5 ${i < Math.round(product.rating) ? "fill-amber-500 text-amber-500" : "text-gray-300"}`} />
                  ))}
                </div>
                <span className="text-xs font-bold text-amber-700">({product.reviews_count} avis)</span>
              </div>
              {soldCount > 0 && (
                <span className="text-sm text-gray-500 inline-flex items-center gap-1">
                  <ShoppingCart className="w-3.5 h-3.5" /> {soldCount} ventes
                </span>
              )}
            </div>

            {/* Price */}
            <div className="mt-5 pb-5 border-b" data-testid="price-block">
              <div className="flex items-end gap-3 flex-wrap">
                <span className="text-3xl md:text-4xl font-extrabold text-[#E3000F]" style={{ fontFamily: "Outfit" }}>
                  {formatGdes(currentPrice)}
                </span>
                {hasDiscount && (
                  <>
                    <span className="text-lg text-gray-400 line-through">{formatGdes(oldPrice)}</span>
                    <span className="bg-[#E3000F] text-white text-xs font-bold px-2 py-0.5 rounded">-{discountPct}%</span>
                  </>
                )}
              </div>
              <p className={`mt-2 text-sm font-semibold inline-flex items-center gap-1.5 ${product.stock > 0 ? "text-green-600" : "text-red-600"}`} data-testid="stock-pill">
                <span className={`w-2 h-2 rounded-full ${product.stock > 0 ? "bg-green-500" : "bg-red-500"}`}></span>
                {product.stock > 0 ? `En stock (${product.stock} disponibles)` : "Rupture de stock"}
              </p>
            </div>

            {/* Variants (optional based on product) */}
            {product.colors?.length > 0 && (
              <VariantSwatches label="Couleur" values={product.colors} type="color" />
            )}
            {product.capacities?.length > 0 && (
              <VariantSwatches label="Capacité" values={product.capacities} type="chip" />
            )}

            {/* Quantity + CTAs */}
            <div className="mt-6">
              {isCable && cableColors.length > 0 ? (
                <div data-testid="cable-color-picker">
                  <label className="text-xs uppercase tracking-widest text-gray-500 font-semibold">Longueur par couleur (pieds)</label>
                  <p className="text-xs text-gray-500 mt-1 mb-3">Renseignez la quantité par couleur. Prix : {formatGdes(currentPrice)}/pied.</p>
                  <div className="space-y-2">
                    {cableColors.map((c, i) => {
                      const val = Number(colorQty[c.name] || 0);
                      return (
                        <div key={c.name + i} className="flex items-center gap-3 border rounded-lg p-2.5" data-testid={`cable-row-${i}`}>
                          <div className="w-6 h-6 rounded-full border shrink-0" style={{ background: c.hex || "#ccc" }} title={c.name} />
                          <span className="flex-1 text-sm font-semibold text-gray-800">{c.name}</span>
                          <div className="inline-flex items-center border-2 border-gray-200 rounded-md overflow-hidden">
                            <button onClick={() => setColorQty((s) => ({ ...s, [c.name]: Math.max(0, (Number(s[c.name]) || 0) - 1) }))} className="px-2 py-1.5 hover:bg-gray-100" aria-label="moins">
                              <Minus className="w-3.5 h-3.5" />
                            </button>
                            <input
                              type="number"
                              min={0}
                              value={val}
                              onChange={(e) => setColorQty((s) => ({ ...s, [c.name]: Math.max(0, Number(e.target.value) || 0) }))}
                              className="w-16 text-center font-bold text-sm outline-none"
                              data-testid={`cable-qty-${i}`}
                            />
                            <button onClick={() => setColorQty((s) => ({ ...s, [c.name]: (Number(s[c.name]) || 0) + 1 }))} className="px-2 py-1.5 hover:bg-gray-100" aria-label="plus">
                              <Plus className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  {(() => {
                    const total = Object.values(colorQty).reduce((s, v) => s + (Number(v) || 0), 0);
                    return (
                      <div className="mt-3 flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2 text-sm">
                        <span className="font-semibold text-gray-700">Total : <b>{total}</b> pied(s)</span>
                        <span className="font-extrabold text-[#0052CC]" style={{ fontFamily: "Outfit" }}>{formatGdes(total * currentPrice)}</span>
                      </div>
                    );
                  })()}
                </div>
              ) : (
                <>
                  <label className="text-xs uppercase tracking-widest text-gray-500 font-semibold">Quantité{product.unit_of_sale && product.unit_of_sale !== "unit" ? ` (${{"case":"par caisse","foot":"par pied","meter":"par mètre","kg":"par kg"}[product.unit_of_sale] || ""})` : ""}</label>
                  <div className="flex flex-wrap items-center gap-3 mt-2">
                    <div className="inline-flex items-center border-2 border-gray-200 rounded-lg overflow-hidden">
                      <button onClick={() => setQty((q) => Math.max(1, q - 1))} className="px-3 py-2.5 hover:bg-gray-100 transition" aria-label="moins" data-testid="qty-minus">
                        <Minus className="w-4 h-4" />
                      </button>
                      <span className="px-5 py-2.5 font-bold min-w-[3rem] text-center" data-testid="qty-value">{qty}</span>
                      <button onClick={() => setQty((q) => q + 1)} className="px-3 py-2.5 hover:bg-gray-100 transition" aria-label="plus" data-testid="qty-plus">
                        <Plus className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </>
              )}
              {(product.options || []).length > 0 && (
                <div className="mt-4 border-2 border-purple-200 bg-purple-50/40 rounded-lg p-3 space-y-3" data-testid="pdp-options">
                  {product.options.map((opt, i) => (
                    (opt.values || []).length > 0 && (
                      <div key={i} data-testid={`pdp-option-${i}`}>
                        <label className="text-xs uppercase tracking-widest text-gray-600 font-semibold">{opt.name}</label>
                        <div className="mt-2 flex flex-wrap gap-2">
                          {opt.values.map((v, k) => {
                            const active = selectedOptions[opt.name] === v;
                            return (
                              <button
                                key={k}
                                type="button"
                                onClick={() => setSelectedOptions((s) => ({ ...s, [opt.name]: v }))}
                                className={`px-3 py-1.5 rounded-full border text-sm font-semibold transition ${active ? "bg-[#0052CC] text-white border-[#0052CC]" : "bg-white text-gray-700 hover:bg-gray-50 border-gray-200"}`}
                                data-testid={`pdp-option-${i}-value-${k}`}
                              >
                                {v}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )
                  ))}
                </div>
              )}
              {product.technician_enabled && (
                <div className="mt-4 border-2 border-amber-200 bg-amber-50/50 rounded-lg p-3" data-testid="pdp-technician">
                  <label className="text-xs uppercase tracking-widest text-gray-600 font-semibold">Installation par technicien</label>
                  <p className="text-xs text-gray-600 mt-1">Prix : <b>{formatGdes(product.technician_price || 0)}</b> par technicien.</p>
                  <div className="mt-2 grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() => setTechnicianQty(0)}
                      className={`px-3 py-2 rounded border text-sm font-semibold transition ${technicianQty === 0 ? "bg-[#0052CC] text-white border-[#0052CC]" : "bg-white text-gray-700 hover:bg-gray-50"}`}
                      data-testid="technician-none"
                    >
                      Sans technicien
                    </button>
                    <button
                      type="button"
                      onClick={() => setTechnicianQty(1)}
                      className={`px-3 py-2 rounded border text-sm font-semibold transition ${technicianQty === 1 ? "bg-[#0052CC] text-white border-[#0052CC]" : "bg-white text-gray-700 hover:bg-gray-50"}`}
                      data-testid="technician-one"
                    >
                      1 Technicien (+{formatGdes(product.technician_price || 0)})
                    </button>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
                <button
                  onClick={handleAddToCart}
                  disabled={product.stock <= 0}
                  className="inline-flex items-center justify-center gap-2 bg-[#0052CC] hover:bg-[#003D99] text-white font-bold py-3.5 rounded-lg transition shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="add-to-cart-btn"
                >
                  <ShoppingCart className="w-5 h-5" /> Ajouter au panier
                </button>
                <button
                  onClick={handleBuyNow}
                  disabled={product.stock <= 0}
                  className="inline-flex items-center justify-center gap-2 bg-[#E3000F] hover:bg-[#B3000C] text-white font-bold py-3.5 rounded-lg transition shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="buy-now-btn"
                >
                  <Zap className="w-5 h-5" /> Acheter maintenant
                </button>
              </div>

              <div className="grid grid-cols-2 gap-3 mt-3">
                <button
                  onClick={() => fav.toggle(product)}
                  className={`inline-flex items-center justify-center gap-2 border-2 rounded-lg py-2.5 text-sm font-semibold transition ${fav.has(product.product_id) ? "border-[#E3000F] text-[#E3000F] bg-red-50" : "border-gray-200 text-gray-700 hover:border-gray-300"}`}
                  data-testid="fav-btn"
                >
                  <Heart className={`w-4 h-4 ${fav.has(product.product_id) ? "fill-current" : ""}`} /> {fav.has(product.product_id) ? "Ajouté" : "Favoris"}
                </button>
                <button
                  onClick={() => cmp.toggle(product)}
                  className={`inline-flex items-center justify-center gap-2 border-2 rounded-lg py-2.5 text-sm font-semibold transition ${cmp.has(product.product_id) ? "border-[#0052CC] text-[#0052CC] bg-blue-50" : "border-gray-200 text-gray-700 hover:border-gray-300"}`}
                  data-testid="cmp-btn"
                >
                  <GitCompare className="w-4 h-4" /> {cmp.has(product.product_id) ? "Ajouté" : "Comparer"}
                </button>
              </div>

              <ShareButtons product={product} />
            </div>
          </div>
        </div>

        {/* Trust badges row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6" data-testid="trust-badges">
          <TrustBadge icon={Truck} title="Livraison rapide" subtitle="Partout en Haïti" color="#0052CC" />
          <TrustBadge icon={ShieldCheck} title="Paiement sécurisé" subtitle="100% sécurisé" color="#16A34A" />
          <TrustBadge icon={Award} title="Garantie 12 mois" subtitle="Pièces et service" color="#F59E0B" />
          <TrustBadge icon={RefreshCcw} title="Retour facile" subtitle="Sous 7 jours" color="#E3000F" />
        </div>

        {/* Tabs section */}
        <div className="grid lg:grid-cols-[minmax(0,1fr)_320px] gap-6 mt-8">
          <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
            <div className="flex overflow-x-auto border-b border-gray-200" data-testid="tabs">
              <TabButton active={tab === "description"} onClick={() => setTab("description")} testid="tab-description">Description</TabButton>
              <TabButton active={tab === "specs"} onClick={() => setTab("specs")} testid="tab-specs">Caractéristiques</TabButton>
              <TabButton active={tab === "reviews"} onClick={() => setTab("reviews")} testid="tab-reviews-tab">
                Avis clients {product.reviews_count > 0 && <span className="ml-1 bg-[#E3000F] text-white text-[10px] px-1.5 rounded-full">{product.reviews_count}</span>}
              </TabButton>
              <TabButton active={tab === "qa"} onClick={() => setTab("qa")} testid="tab-qa">Questions & Réponses</TabButton>
            </div>

            <div className="p-5 md:p-6">
              {tab === "description" && (
                <div className="prose prose-sm max-w-none text-gray-700 leading-relaxed whitespace-pre-line" data-testid="tab-content-description">
                  {product.description || "Aucune description disponible pour ce produit."}
                </div>
              )}

              {tab === "specs" && (
                <div data-testid="tab-content-specs">
                  <table className="w-full text-sm">
                    <tbody>
                      {product.brand && <SpecRow label="Marque" value={product.brand} />}
                      <SpecRow label="Catégorie" value={catLabel} />
                      <SpecRow label="SKU" value={product.product_id.slice(-10).toUpperCase()} mono />
                      <SpecRow label="Stock" value={`${product.stock} unité(s)`} />
                      <SpecRow label="Prix" value={formatGdes(currentPrice)} />
                      {hasDiscount && <SpecRow label="Prix d'origine" value={formatGdes(oldPrice)} />}
                      <SpecRow label="Note moyenne" value={`${product.rating?.toFixed?.(1) || product.rating} / 5 (${product.reviews_count} avis)`} />
                    </tbody>
                  </table>
                </div>
              )}

              {tab === "reviews" && (
                <div className="space-y-4" data-testid="tab-content-reviews">
                  {reviews.length === 0 ? (
                    <p className="text-gray-500 text-sm">Aucun avis pour le moment. Soyez le premier à donner votre opinion !</p>
                  ) : (
                    reviews.map((r) => (
                      <div key={r.review_id} className="border border-gray-100 rounded-xl p-4 bg-gray-50">
                        <div className="flex items-center justify-between flex-wrap gap-2">
                          <div className="flex items-center gap-2">
                            <div className="w-9 h-9 rounded-full bg-[#0052CC] text-white text-sm font-bold flex items-center justify-center">
                              {(r.name || "?")[0].toUpperCase()}
                            </div>
                            <div>
                              <p className="font-semibold text-sm">{r.name}</p>
                              <p className="text-xs text-gray-500">{new Date(r.created_at).toLocaleDateString("fr-FR")}</p>
                            </div>
                          </div>
                          <div className="flex">
                            {Array.from({ length: 5 }).map((_, i) => (
                              <Star key={i} className={`w-3.5 h-3.5 ${i < r.rating ? "fill-amber-500 text-amber-500" : "text-gray-300"}`} />
                            ))}
                          </div>
                        </div>
                        {r.comment && <p className="text-sm text-gray-700 mt-3 leading-relaxed">{r.comment}</p>}
                        {r.images?.length > 0 && (
                          <div className="flex flex-wrap gap-2 mt-3" data-testid={`review-photos-${r.review_id}`}>
                            {r.images.map((img, i) => (
                              <button
                                key={i}
                                type="button"
                                onClick={() => setReviewLightbox(reviewImgUrl(img))}
                                className="w-20 h-20 rounded-lg overflow-hidden border-2 border-gray-200 hover:border-[#0052CC] transition"
                              >
                                <img src={reviewImgUrl(img)} alt="" className="w-full h-full object-cover" />
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    ))
                  )}

                  {/* Review form */}
                  <form onSubmit={submitReview} className="border-t pt-6 mt-4" data-testid="review-form">
                    <h3 className="font-bold mb-3" style={{ fontFamily: "Outfit" }}>Laissez votre avis</h3>
                    <div className="flex gap-1 mb-3">
                      {[1, 2, 3, 4, 5].map((n) => (
                        <button type="button" key={n} onClick={() => setReviewRating(n)}>
                          <Star className={`w-7 h-7 ${n <= reviewRating ? "fill-amber-500 text-amber-500" : "text-gray-300"}`} />
                        </button>
                      ))}
                    </div>
                    <textarea
                      value={reviewComment}
                      onChange={(e) => setReviewComment(e.target.value)}
                      rows={3}
                      placeholder="Partagez votre expérience avec ce produit…"
                      className="w-full border-2 border-gray-200 rounded-lg p-3 text-sm focus:border-[#0052CC] outline-none"
                      data-testid="review-comment"
                    />
                    <div className="flex flex-wrap gap-2 mt-3">
                      {reviewImages.map((img, i) => (
                        <div key={i} className="relative w-16 h-16 rounded-md overflow-hidden border">
                          <img src={reviewImgUrl(img)} alt="" className="w-full h-full object-cover" />
                          <button
                            type="button"
                            onClick={() => setReviewImages(reviewImages.filter((_, k) => k !== i))}
                            className="absolute -top-1 -right-1 bg-red-600 text-white rounded-full w-5 h-5 text-xs font-bold"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                      {reviewImages.length < 4 && (
                        <button
                          type="button"
                          onClick={() => reviewFileRef.current?.click()}
                          disabled={uploadingReview}
                          className="w-16 h-16 border-2 border-dashed border-gray-300 rounded-md flex flex-col items-center justify-center text-gray-400 hover:border-[#0052CC] hover:text-[#0052CC] disabled:opacity-50"
                          data-testid="add-review-photo"
                        >
                          <Camera className="w-4 h-4" />
                          <span className="text-[9px] mt-0.5">{uploadingReview ? "…" : "Photo"}</span>
                        </button>
                      )}
                    </div>
                    <input ref={reviewFileRef} type="file" accept="image/*" multiple onChange={onPickReviewPhotos} className="hidden" data-testid="review-photo-input" />
                    <button type="submit" className="mt-4 bg-[#0052CC] hover:bg-[#003D99] text-white font-bold px-5 py-2.5 rounded-lg transition" data-testid="review-submit">
                      Publier l&apos;avis
                    </button>
                  </form>
                </div>
              )}

              {tab === "qa" && (
                <div className="text-center py-10" data-testid="tab-content-qa">
                  <MessageCircle className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-600 font-semibold">Aucune question pour le moment</p>
                  <p className="text-sm text-gray-500 mt-1 mb-4">Une interrogation sur ce produit ? Contactez-nous, notre équipe vous répond sous 24h.</p>
                  <a
                    href="https://wa.me/50932190716"
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1FAE55] text-white font-bold px-5 py-2.5 rounded-lg transition"
                  >
                    <MessageCircle className="w-4 h-4" /> Poser une question sur WhatsApp
                  </a>
                </div>
              )}
            </div>
          </div>

          {/* Right sidebar — Delivery info */}
          <aside className="bg-white border border-gray-200 rounded-2xl p-5 h-fit" data-testid="delivery-sidebar">
            <h3 className="font-bold text-base mb-4" style={{ fontFamily: "Outfit" }}>Informations de livraison</h3>
            <SidebarItem icon={Package} title="Livraison à domicile" subtitle="1 à 3 jours ouvrés" color="#0052CC" />
            <SidebarItem icon={CreditCard} title="Paiement à la livraison disponible" subtitle="Espèces ou mobile money" color="#16A34A" isLink />
            <SidebarItem icon={Truck} title={<>Livraison gratuite à partir de <b>10 000 HTG</b></>} subtitle="Sur commandes éligibles" color="#E3000F" />
            <SidebarItem icon={Clock} title="Assistance 24/7" subtitle="Toujours à votre écoute" color="#8B5CF6" isLast />
          </aside>
        </div>

        {/* Similar products */}
        {similar.length > 0 && (
          <ProductRow
            title="Produits similaires"
            products={similar}
            testid="similar-section"
            viewAllHref={`/products?category=${product.category}`}
          />
        )}

        {/* Recently viewed */}
        {recent.length > 0 && (
          <ProductRow
            title="Récemment consultés"
            products={recent}
            testid="recent-section"
            viewAllHref="/products"
          />
        )}
      </div>

      {/* Fullscreen lightbox for main image */}
      {lightbox && (
        <div className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4" onClick={() => setLightbox(false)} data-testid="image-lightbox">
          <button className="absolute top-4 right-4 text-white bg-white/10 rounded-full p-2 hover:bg-white/20" onClick={() => setLightbox(false)}>
            <XIcon className="w-6 h-6" />
          </button>
          <img src={productImageUrl(images[activeImg])} alt="" className="max-h-full max-w-full object-contain" />
        </div>
      )}

      {reviewLightbox && (
        <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4" onClick={() => setReviewLightbox(null)} data-testid="review-lightbox">
          <button className="absolute top-4 right-4 text-white bg-white/10 rounded-full p-2 hover:bg-white/20" onClick={() => setReviewLightbox(null)}>
            <XIcon className="w-6 h-6" />
          </button>
          <img src={reviewLightbox} alt="" className="max-h-full max-w-full object-contain" />
        </div>
      )}
    </div>
  );
}

/* ---------- Sub-components ---------- */

function ShareButtons({ product }) {
  const [copied, setCopied] = useState(false);
  const { format: formatGdes } = useCurrency();
  const shareUrl = typeof window !== "undefined" ? window.location.href : "";
  const shareText = `Découvrez ${product.name} sur John Elektwo — ${formatGdes(product.is_promo && product.discount_percent > 0 ? Math.round(product.price * (100 - product.discount_percent)) / 100 : product.price)}`;
  const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(`${shareText}\n${shareUrl}`)}`;
  const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(shareText)}`;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success("Lien copié !");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Impossible de copier le lien");
    }
  };

  return (
    <div className="mt-5 pt-5 border-t border-gray-100" data-testid="share-buttons">
      <p className="text-xs uppercase tracking-widest text-gray-500 font-semibold mb-3 inline-flex items-center gap-1.5">
        <Share2 className="w-3.5 h-3.5" /> Partager avec un ami
      </p>
      <div className="flex flex-wrap gap-2">
        <a
          href={whatsappUrl}
          target="_blank"
          rel="noreferrer noopener"
          className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1FAE55] text-white font-semibold px-4 py-2.5 rounded-lg text-sm transition shadow-sm"
          data-testid="share-whatsapp"
          aria-label="Partager sur WhatsApp"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.966-.273-.101-.472-.15-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.007-.371-.009-.57-.009-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
          </svg>
          WhatsApp
        </a>
        <a
          href={facebookUrl}
          target="_blank"
          rel="noreferrer noopener"
          className="inline-flex items-center gap-2 bg-[#1877F2] hover:bg-[#0e5fc9] text-white font-semibold px-4 py-2.5 rounded-lg text-sm transition shadow-sm"
          data-testid="share-facebook"
          aria-label="Partager sur Facebook"
        >
          <Facebook className="w-4 h-4" fill="currentColor" />
          Facebook
        </a>
        <button
          onClick={copyLink}
          className={`inline-flex items-center gap-2 border-2 font-semibold px-4 py-2.5 rounded-lg text-sm transition ${copied ? "border-green-500 text-green-600 bg-green-50" : "border-gray-200 text-gray-700 hover:border-gray-300"}`}
          data-testid="share-copy-link"
          aria-label="Copier le lien"
        >
          {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
          {copied ? "Copié" : "Copier le lien"}
        </button>
      </div>
    </div>
  );
}

function VariantSwatches({ label, values, type }) {
  const [selected, setSelected] = useState(values[0]);
  return (
    <div className="mt-5">
      <p className="text-xs uppercase tracking-widest text-gray-500 font-semibold mb-2">{label} :</p>
      <div className="flex flex-wrap gap-2">
        {values.map((v) => (
          <button
            key={v}
            onClick={() => setSelected(v)}
            className={`transition ${type === "color"
              ? `w-9 h-9 rounded-full border-2 ${selected === v ? "border-[#0052CC]" : "border-gray-200"}`
              : `px-4 py-1.5 rounded-lg border-2 text-sm font-semibold ${selected === v ? "border-[#0052CC] text-[#0052CC] bg-blue-50" : "border-gray-200 text-gray-700"}`}`}
            style={type === "color" ? { backgroundColor: v } : undefined}
            aria-label={`${label} ${v}`}
          >
            {type === "chip" && v}
          </button>
        ))}
      </div>
    </div>
  );
}

function TrustBadge({ icon: Icon, title, subtitle, color }) {
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-3 flex items-center gap-3 hover:shadow-md transition">
      <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${color}15`, color }}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="min-w-0">
        <p className="text-sm font-bold text-gray-900 truncate">{title}</p>
        <p className="text-xs text-gray-500 truncate">{subtitle}</p>
      </div>
    </div>
  );
}

function TabButton({ active, onClick, children, testid }) {
  return (
    <button
      onClick={onClick}
      className={`relative px-5 py-3.5 text-sm font-bold whitespace-nowrap transition ${active ? "text-[#0052CC]" : "text-gray-500 hover:text-gray-800"}`}
      data-testid={testid}
    >
      {children}
      {active && <span className="absolute bottom-0 left-4 right-4 h-0.5 bg-[#0052CC] rounded-full"></span>}
    </button>
  );
}

function SpecRow({ label, value, mono }) {
  return (
    <tr className="border-b border-gray-100">
      <td className="py-2.5 pr-4 text-gray-500 font-semibold text-xs uppercase tracking-widest w-1/3">{label}</td>
      <td className={`py-2.5 text-gray-900 ${mono ? "font-mono" : "font-semibold"}`}>{value}</td>
    </tr>
  );
}

function SidebarItem({ icon: Icon, title, subtitle, color, isLink, isLast }) {
  return (
    <div className={`flex items-start gap-3 py-3 ${isLast ? "" : "border-b border-gray-100"}`}>
      <div className="w-9 h-9 rounded-lg shrink-0 flex items-center justify-center" style={{ background: `${color}15`, color }}>
        <Icon className="w-4 h-4" />
      </div>
      <div className="min-w-0 flex-1">
        <p className={`text-sm ${isLink ? "text-[#0052CC] font-bold" : "text-gray-900 font-semibold"}`}>{title}</p>
        {subtitle && <p className="text-xs text-gray-500">{subtitle}</p>}
      </div>
    </div>
  );
}

function ProductRow({ title, products, testid, viewAllHref }) {
  return (
    <section className="mt-16 md:mt-20 pt-2" data-testid={testid}>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <span className="w-1.5 h-7 bg-[#E3000F] rounded-sm"></span>
          <h2 className="text-xl md:text-2xl font-extrabold tracking-tight text-left" style={{ fontFamily: "Outfit" }}>{title}</h2>
        </div>
        {viewAllHref && (
          <Link to={viewAllHref} className="text-sm font-bold text-[#0052CC] hover:underline inline-flex items-center gap-1">
            Voir tout <ChevronRight className="w-4 h-4" />
          </Link>
        )}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3 md:gap-4">
        {products.map((p) => (
          <ProductCard key={p.product_id} product={p} />
        ))}
      </div>
    </section>
  );
}
